import { LoadingSection } from '../../common/LoadingSection';
import {
    Box,
    ButtonGroup,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    Divider,
    Grid,
    Stack,
    Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useDataProvider } from 'react-admin';
import Button from '@mui/material/Button';
import { ChevronLeft } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import useQuestionPaper from '../../hooks/useQuestionPaper';
import { nameToCode } from '../../services/languageUtils';

const QuestionPaperView = ({ testId }: any) => {
    const navigate = useNavigate();
    const params = useParams();
    const resolvedTestId = testId || params.testId || params.exerciseId || params.id;
    const [showSolutions, setShowSolutions] = useState(false);
    const [activeLang, setActiveLang] = useState<string | null>(null);
    const [activeLangCode, setActiveLangCode] = useState<string | null>(null);
    const { questionPaper, loading, error } = useQuestionPaper(
        resolvedTestId,
        undefined,
        activeLangCode || undefined,
    );
    const paper = Array.isArray(questionPaper) ? questionPaper[0] : questionPaper;
    const languages: string[] = useMemo(() => paper?.languages ?? [], [paper]);
    const title = 'Test Preview';

    useEffect(() => {
        if (!activeLang && languages.length > 0) {
            setActiveLang(languages[0]);
        }
    }, [languages, activeLang]);
    const dataProvider = useDataProvider();
    const { data: sectionsData } = useQuery(
        ['sections', 'getList', resolvedTestId],
        () =>
            dataProvider.getList('sections', {
                filter: { testId: resolvedTestId },
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'id', order: 'ASC' },
            }),
        { enabled: !!resolvedTestId },
    );
    const sections: any[] = Array.isArray(sectionsData?.data)
        ? sectionsData?.data ?? []
        : [];
    const sectionIdToCopy = sections.length === 1 ? sections[0]?.id : null;
    const showCopyButton = sections.length === 1;
    const headerCopyDisabled = !sectionIdToCopy;
    const handleCopySectionId = (id: string) => {
        navigator.clipboard.writeText(id);
    };

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            if (!activeLang) {
                setActiveLangCode(null);
                return;
            }
            try {
                const code = await nameToCode(activeLang);
                if (!cancelled) setActiveLangCode(code);
            } catch (e) {
                if (!cancelled) setActiveLangCode(activeLang);
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [activeLang]);

    if (loading) {
        return <LoadingSection />;
    }

    if (error) {
        return (
            <div className="px-4 py-4">
                <Typography variant={'h6'} color="error">
                    Error loading question paper
                </Typography>
                <Typography variant={'body2'} color="textSecondary">
                    {error}
                </Typography>
                <Button onClick={() => navigate(-1)} sx={{ marginTop: '10px' }}>
                    <ChevronLeft /> Back
                </Button>
            </div>
        );
    }

    if (!paper) {
        return (
            <div className="px-4 py-4">
                <Typography variant={'h6'}>No question paper found</Typography>
                <Button onClick={() => navigate(-1)} sx={{ marginTop: '10px' }}>
                    <ChevronLeft /> Back
                </Button>
            </div>
        );
    }

    return (
        <div className="px-4">
            <Box
                sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: 'background.paper', pb: 1 }}
            >
                <div className="flex flex-col gap-1">
                    <div className="relative flex items-center justify-between pt-4">
                        <Button onClick={() => navigate(-1)}>
                            <ChevronLeft /> Back
                        </Button>
                        <p className="absolute inset-x-0 mx-auto text-center text-xl font-semibold">
                            {title}
                        </p>
                        <div className="flex items-center gap-2">
                            {showCopyButton && (
                                <Button
                                    size="small"
                                    variant="text"
                                    disabled={headerCopyDisabled}
                                    onClick={() => sectionIdToCopy && handleCopySectionId(sectionIdToCopy)}
                                    sx={{ border: 'none', minWidth: 'auto', px: 1 }}
                                >
                                    Copy SectionID
                                </Button>
                            )}
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => setShowSolutions(s => !s)}
                                sx={{ border: 'none', minWidth: 'auto', px: 1 }}
                            >
                                {showSolutions ? 'Hide Solutions' : 'Show Solutions'}
                            </Button>
                        </div>
                    </div>

                    <div className="flex flex-col items-center ">
                        <p className="text-lg font-medium ">{paper?.name}</p>
                    </div>
                    <div className="flex flex-row gap-3 justify-center ">
                        {paper?.totalMarks !== undefined && <p> Marks: {paper?.totalMarks} </p>}
                        {paper?.totalQuestions !== undefined && (
                            <p> Questions: {paper?.totalQuestions} </p>
                        )}
                        {paper?.maxDurationInMinutes !== undefined && (
                            <p> Duration: {paper?.maxDurationInMinutes} min</p>
                        )}
                    </div>

                    {languages.length > 0 && (
                        <Stack alignItems="center">
                            <ButtonGroup
                                size="small"
                                variant="outlined"
                                aria-label="language selector"
                            >
                                {languages.map(lang => (
                                    <Button
                                        key={lang}
                                        onClick={() => setActiveLang(lang)}
                                        variant={activeLang === lang ? 'contained' : 'outlined'}
                                    >
                                        {lang.toUpperCase()}
                                    </Button>
                                ))}
                            </ButtonGroup>
                        </Stack>
                    )}
                </div>
            </Box>

            <Divider />
            <Box sx={{ mt: 2 }}>
                <Box sx={{ background: 'rgba(0,0,0,0.2)', p: '2px 10px', mb: 1 }}>
                    <Typography variant="h6">General Instructions</Typography>
                </Box>
                {paper?.generalInstructions ? (
                    <div dangerouslySetInnerHTML={{ __html: paper.generalInstructions }} />
                ) : (
                    <Typography variant={'body2'} color="textSecondary">
                        No general instructions available
                    </Typography>
                )}
            </Box>

            <Divider sx={{ my: 2 }} />
            <Box>
                <Box sx={{ background: 'rgba(0,0,0,0.2)', p: '2px 10px', mb: 1 }}>
                    <Typography variant="h6">Test Instructions</Typography>
                </Box>
                {paper?.testInstructions ? (
                    <div dangerouslySetInnerHTML={{ __html: paper.testInstructions }} />
                ) : (
                    <Typography variant={'body2'} color="textSecondary">
                        No test instructions available
                    </Typography>
                )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {paper?.sections?.length ? (
                <Box>
                    {paper.sections.map((section: any, si: number) => (
                        <Box key={section.id ?? si} sx={{ mb: 3 }}>
                            <div className="bg-gray-100 p-2">
                                {section?.name || `Section ${si + 1}`}
                            </div>
                            <Stack spacing={2}>
                                {section.questions?.map((q: any, qi: number) => (
                                    <>
                                        <Box key={q.id ?? qi}>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={1}
                                                sx={{ mb: 1 }}
                                            >
                                                <Typography variant="subtitle1">
                                                    {qi + 1}.
                                                </Typography>
                                                <div
                                                    dangerouslySetInnerHTML={{
                                                        __html: q.content || '',
                                                    }}
                                                />
                                            </Stack>
                                            {q?.image ? (
                                                <Box sx={{ mb: 1 }}>
                                                    <img
                                                        src={q.image}
                                                        alt="question"
                                                        style={{ maxWidth: '100%' }}
                                                    />
                                                </Box>
                                            ) : null}
                                            <Grid container spacing={1}>
                                                {(q.options || []).map((opt: any) => {
                                                    const highlight =
                                                        showSolutions && !!opt.isCorrect;
                                                    return (
                                                        <Grid item xs={12} md={6} key={opt.id}>
                                                            <Card
                                                                variant="outlined"
                                                                sx={{
                                                                    borderColor: highlight
                                                                        ? 'success.main'
                                                                        : 'divider',
                                                                    backgroundColor: highlight
                                                                        ? 'rgba(56,142,60,0.08)'
                                                                        : 'inherit',
                                                                }}
                                                            >
                                                                <CardActionArea disableRipple>
                                                                    <CardContent>
                                                                        {opt.image ? (
                                                                            <Box sx={{ mb: 1 }}>
                                                                                <img
                                                                                    src={opt.image}
                                                                                    alt="option"
                                                                                    style={{
                                                                                        maxWidth:
                                                                                            '100%',
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                        ) : null}
                                                                        <div
                                                                            dangerouslySetInnerHTML={{
                                                                                __html:
                                                                                    opt.content ||
                                                                                    '',
                                                                            }}
                                                                        />
                                                                    </CardContent>
                                                                </CardActionArea>
                                                            </Card>
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                            {showSolutions && q.solutionDescription ? (
                                                <Box
                                                    sx={{
                                                        mt: 1,
                                                        p: 1,
                                                        backgroundColor: 'rgba(0,0,0,0.05)',
                                                    }}
                                                >
                                                    <Typography variant="subtitle2">
                                                        Solution Description:
                                                    </Typography>
                                                    <div
                                                        dangerouslySetInnerHTML={{
                                                            __html: q.solutionDescription,
                                                        }}
                                                    />
                                                </Box>
                                            ) : null}
                                        </Box>
                                        <p className="border bg-gray-500 h-1 w-64 mb-5 self-center"></p>
                                    </>
                                ))}
                            </Stack>
                        </Box>
                    ))}
                </Box>
            ) : null}
        </div>
    );
};

export default QuestionPaperView;
